import { appInfo, aboutInfo, indexItems, contactInfo, featureItems } from '@/mocks/homeData';

function SectionTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="text-center mb-10 md:mb-14">
      <h2 className="text-2xl md:text-3xl font-black text-foreground-950 tracking-tight">
        {title}
      </h2>
      <p className="mt-3 text-sm md:text-base text-foreground-500">{subtitle}</p>
    </div>
  );
}

export function AppSection() {
  return (
    <section id="app" className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="mx-auto max-w-[1000px]">
        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-14 bg-background-200/80 rounded-2xl border border-background-400/30 p-6 md:p-10">
          <div className="flex-shrink-0 w-48 md:w-56">
            <img
              src={appInfo.image}
              alt="苹果视频APP"
              className="w-full rounded-xl"
            />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl md:text-3xl font-black text-foreground-950 tracking-tight">
              {appInfo.title}
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-500 leading-relaxed">
              {appInfo.description}
            </p>
            <div className="flex flex-wrap gap-2 mt-5 justify-center md:justify-start">
              {appInfo.features.map((feat) => (
                <span
                  key={feat}
                  className="px-3 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-xs font-medium whitespace-nowrap"
                >
                  {feat}
                </span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-center md:justify-start">
              <a
                href="#hero"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#hero')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-foreground-950 font-semibold rounded-lg transition-all duration-200 whitespace-nowrap text-center text-sm"
              >
                <i className="ri-apple-line mr-2"></i>
                iOS 下载
              </a>
              <a
                href="#hero"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#hero')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-6 py-3 bg-background-400/60 hover:bg-background-400 border border-background-500/50 text-foreground-800 font-medium rounded-lg transition-all duration-200 whitespace-nowrap text-center text-sm"
              >
                <i className="ri-android-line mr-2"></i>
                Android 下载
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function AboutSection() {
  return (
    <section id="about" className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="mx-auto max-w-[900px]">
        <SectionTitle
          title={aboutInfo.title}
          subtitle="汇聚全网优质影视资源，打造极致观影体验"
        />
        <div className="bg-background-200/80 rounded-xl border border-background-400/30 p-6 md:p-10">
          {aboutInfo.paragraphs.map((p, idx) => (
            <p
              key={idx}
              className={`text-sm md:text-base text-foreground-500 leading-relaxed ${
                idx < aboutInfo.paragraphs.length - 1 ? 'mb-4' : ''
              }`}
            >
              {p}
            </p>
          ))}
          <div className="flex flex-wrap gap-4 mt-6 pt-6 border-t border-background-400/20">
            <div className="flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-primary-400"></i>
              <span className="text-sm text-foreground-600">100%免费</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-primary-400"></i>
              <span className="text-sm text-foreground-600">多源聚合</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-primary-400"></i>
              <span className="text-sm text-foreground-600">全平台支持</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-primary-400"></i>
              <span className="text-sm text-foreground-600">安全无广告</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function IndexSection() {
  return (
    <section id="index" className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="mx-auto max-w-[1200px]">
        <SectionTitle
          title="分类索引"
          subtitle="按内容类型快速浏览，精准找到你想看的"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {indexItems.map((item) => (
            <a
              key={item.id}
              href="#cards"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#cards')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="group flex flex-col items-center text-center p-5 bg-background-200/80 rounded-xl border border-background-400/30 hover:border-primary-500/30 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center mb-3 group-hover:bg-primary-500/20 transition-colors">
                <i className={`${item.icon} text-primary-400 text-xl`}></i>
              </div>
              <h3 className="text-sm font-bold text-foreground-800 group-hover:text-primary-400 transition-colors whitespace-nowrap">
                {item.title}
              </h3>
              <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed line-clamp-2">
                {item.desc}
              </p>
              <span className="mt-2 text-xs font-mono text-primary-400 font-semibold">
                {item.count}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ContactSection() {
  return (
    <section id="contact" className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="mx-auto max-w-[700px]">
        <SectionTitle
          title={contactInfo.title}
          subtitle="您的每一条反馈都是我们进步的动力"
        />
        <div className="bg-background-200/80 rounded-xl border border-background-400/30 p-6 md:p-10 text-center">
          <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-8">
            {contactInfo.description}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-background-300/50 border border-background-400/20">
              <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center mx-auto mb-3">
                <i className="ri-mail-line text-primary-400 text-lg"></i>
              </div>
              <div className="text-xs text-foreground-500 mb-1">电子邮件</div>
              <div className="text-sm font-semibold text-foreground-800 font-mono break-all">
                {contactInfo.email}
              </div>
            </div>
            <div className="p-4 rounded-xl bg-background-300/50 border border-background-400/20">
              <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center mx-auto mb-3">
                <i className="ri-qq-line text-accent-400 text-lg"></i>
              </div>
              <div className="text-xs text-foreground-500 mb-1">QQ群号</div>
              <div className="text-sm font-semibold text-foreground-800 font-mono">
                {contactInfo.qq}
              </div>
            </div>
            <div className="p-4 rounded-xl bg-background-300/50 border border-background-400/20">
              <div className="w-10 h-10 rounded-lg bg-secondary-500/10 flex items-center justify-center mx-auto mb-3">
                <i className="ri-wechat-line text-secondary-400 text-lg"></i>
              </div>
              <div className="text-xs text-foreground-500 mb-1">微信公众号</div>
              <div className="text-sm font-semibold text-foreground-800 font-mono">
                {contactInfo.wechat}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FeaturesSection() {
  return (
    <section id="features" className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="mx-auto max-w-[1000px]">
        <SectionTitle
          title="平台优势"
          subtitle="为什么千万用户选择苹果视频？三大核心优势告诉你答案"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {featureItems.map((item) => (
            <div
              key={item.id}
              className="group p-6 bg-background-200/80 rounded-xl border border-background-400/30 hover:border-primary-500/20 hover:-translate-y-1 transition-all duration-300 text-center"
            >
              <div className="w-14 h-14 rounded-2xl bg-primary-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary-500/20 transition-colors">
                <i className={`${item.icon} text-primary-400 text-2xl`}></i>
              </div>
              <h3 className="text-base font-bold text-foreground-800 mb-2 whitespace-nowrap">
                {item.title}
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}